import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../lib/useSeo';
import { API_BASE_URL, APP_URL } from '../config/api';
import { ArchImage, Label } from '../components/System';

const RESEND_COOLDOWN = 60; // seconds; matches OTP_RESEND_COOLDOWN_SECONDS on the server

async function postJson(path, body) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // fetch() rejects with a bare "Failed to fetch" for anything network-level:
    // server down, DNS, CORS preflight refused, offline. None of that is
    // actionable for a visitor, so say something they can actually act on.
    throw new Error(
      "We couldn't reach the server. Check your connection and try again — if it keeps happening, email hello@sidwell.ai."
    );
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON error body */
  }
  if (!res.ok) {
    const detail = data?.detail;
    // FastAPI validation errors arrive as an array of objects, not a string.
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg.replace(/^Value error, /, '')
        : 'Something went wrong. Please try again.';
    throw new Error(message);
  }
  return data;
}

function SignIn() {
  useSeo({
    title: 'Sign in',
    path: '/signin',
    description: 'Sign in to Sidwell with a one-time code sent to your email.',
    noindex: true,
  });

  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | verifying
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef(null);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  const sendCode = async (e) => {
    e?.preventDefault();
    setError('');
    setStatus('sending');
    try {
      await postJson('/api/auth/otp/request', { email: email.trim() });
      setStep('code');
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      setError(err.message);
    } finally {
      setStatus('idle');
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('verifying');
    try {
      const { token } = await postJson('/api/auth/otp/verify', {
        email: email.trim(),
        code,
      });
      // Hand the token to the app in the URL fragment — fragments are never
      // sent to a server, so it stays out of access logs and referrers.
      window.location.replace(`${APP_URL}/#t=${encodeURIComponent(token)}`);
    } catch (err) {
      setError(err.message);
      setCode('');
      setStatus('idle');
      codeInputRef.current?.focus();
    }
  };

  return (
    <main className="relative w-full">
      <section className="grid min-h-[calc(100vh-72px)] grid-cols-1 bg-limestone lg:grid-cols-2">
        <div className="flex items-center justify-center px-5 py-16 sm:px-10 lg:py-20">
          <div className="w-full max-w-[460px]">
            <Label className="mb-5">Sign in</Label>
            <h1 className="type-h1-compact">{step === 'email' ? 'Welcome back.' : 'Check your email.'}</h1>
            <p className="mt-4 text-glass">
              {step === 'email' ? (
                <>No password to remember — we&rsquo;ll email you a six-digit code.</>
              ) : (
                <>
                  We sent a six-digit code to <span className="font-semibold text-wall">{email}</span>. It expires in ten minutes.
                </>
              )}
            </p>

            <div className="statement mt-8 px-5 pb-6 pt-5 shadow-panel sm:px-7 sm:pb-7">
              {step === 'email' ? (
                <form onSubmit={sendCode} noValidate>
                  <label className="block">
                    <span className="mb-1.5 block text-[14px] font-semibold">Work email</span>
                    <input
                      className="field"
                      type="email"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      required
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </label>

                  {error && <p role="alert" className="mt-4 text-[15px] text-oxide">{error}</p>}

                  <button type="submit" disabled={status === 'sending' || !email.trim()} className="btn-primary mt-6 w-full">
                    {status === 'sending' ? 'Sending…' : 'Email me a code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyCode} noValidate>
                  <label className="block">
                    <span className="mb-1.5 block text-[14px] font-semibold">Six-digit code</span>
                    <input
                      ref={codeInputRef}
                      className="field text-center !text-[26px] tracking-[0.4em] figure"
                      type="text"
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]*"
                      maxLength={6}
                      required
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </label>

                  {error && <p role="alert" className="mt-4 text-[15px] text-oxide">{error}</p>}

                  <button type="submit" disabled={status === 'verifying' || code.length !== 6} className="btn-primary mt-6 w-full">
                    {status === 'verifying' ? 'Signing you in…' : 'Sign in'}
                  </button>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 text-[14px]">
                    <button
                      type="button"
                      onClick={() => {
                        setStep('email');
                        setCode('');
                        setError('');
                      }}
                      className="min-h-[44px] font-medium text-glass transition-colors hover:text-wall"
                    >
                      Use a different email
                    </button>
                    <button
                      type="button"
                      onClick={sendCode}
                      disabled={cooldown > 0 || status === 'sending'}
                      className="min-h-[44px] font-medium text-glass transition-colors hover:text-wall disabled:cursor-not-allowed disabled:hover:text-glass figure"
                    >
                      {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <p className="mt-6 text-[14px] text-glass">
              By signing in you agree to our <Link className="link-inline" to="/terms">Terms of Service</Link>. Trouble getting in?{' '}
              <a className="link-inline" href="mailto:hello@sidwell.ai">Email us</a>.
            </p>
          </div>
        </div>
        <ArchImage name="colonnade" priority sizes="50vw" className="hidden lg:block" />
      </section>
    </main>
  );
}

export default SignIn;
